import {
  PerfusionScanModel,
  SCAN_PAGE_SIZE,
  type IPerfusionScan,
  type IPerfusionScansResponse
} from '@/models/perfusionScan';
import { mapScannedFields, type IScannedFieldsResponse, type ScannedField } from '@/models/scannedField';
import { signedApiClient } from './apiClient';

/** What recording an application sends: the entry, its form, and the answers still holding what the scan wrote. */
export interface PerfusionScanApplicationBody {
  entryId: string;
  formId: string;
  variables: string[];
  /** Each of `variables`' answers exactly as the entry saves it. Its keys are `variables`. */
  answers: Record<string, string>;
}

type PerfusionScansPayload = {
  scans: PerfusionScanModel[];
  total: number;
};

class PerfusionScanService {
  private readonly pumpEndpoint: string = 'pump';

  /** `GET pump/:pumpCenterId/case/:perfusionId/scan` — the case's scans, newest first. */
  async getScans(
    pumpCenterId: string,
    perfusionId: string,
    limit: number = SCAN_PAGE_SIZE,
    offset: number = 0
  ): Promise<PerfusionScansPayload> {
    // The signature covers the request line, so the query string has to be part of the signed uri.
    const uri = `${this.pumpEndpoint}/${pumpCenterId}/case/${perfusionId}/scan?limit=${limit}&offset=${offset}`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    const responseData = response.data as IPerfusionScansResponse;

    return {
      scans: (responseData.scans ?? []).map(PerfusionScanModel.mapFromBackend),
      total: responseData.total ?? 0
    };
  }

  /**
   * `GET pump/:pumpCenterId/case/:perfusionId/scan/:scanId` — one scan, the body one list item would
   * be. spdata-api answers 404 for a scan the list would not show, a deleted one among them.
   */
  async getScan(pumpCenterId: string, perfusionId: string, scanId: string): Promise<PerfusionScanModel> {
    const uri = `${this.pumpEndpoint}/${pumpCenterId}/case/${perfusionId}/scan/${scanId}`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);

    return PerfusionScanModel.mapFromBackend(response.data as IPerfusionScan);
  }

  /**
   * `POST pump/:pumpCenterId/case/:perfusionId/scan/:scanId/applications` — records that the scan was applied to an
   * entry, with the variables still holding what it wrote and their exact answers (ENG-10005, ADR 0016). Sent at Save,
   * before the entry save. Answers the scan with all its applications.
   */
  async recordApplication(
    pumpCenterId: string,
    perfusionId: string,
    scanId: string,
    application: PerfusionScanApplicationBody
  ): Promise<PerfusionScanModel> {
    const uri = `${this.pumpEndpoint}/${pumpCenterId}/case/${perfusionId}/scan/${scanId}/applications`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri, application);

    return PerfusionScanModel.mapFromBackend(response.data as IPerfusionScan);
  }

  /**
   * `GET pump/:pumpCenterId/case/:perfusionId/entry/:entryId/scan-applications` — the entry's scanned fields: each field
   * whose saved answer still equals what an application of a scan wrote, with that scan, deleted ones included
   * (ENG-10005, ADR 0016).
   */
  async getScannedFields(pumpCenterId: string, perfusionId: string, entryId: string): Promise<ScannedField[]> {
    const uri = `${this.pumpEndpoint}/${pumpCenterId}/case/${perfusionId}/entry/${entryId}/scan-applications`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);

    return mapScannedFields(response.data as IScannedFieldsResponse);
  }

  /**
   * `DELETE pump/:pumpCenterId/case/:perfusionId/scan/:scanId` — archives the scan, which leaves the
   * case's list. The entries it was applied to keep their values. 204, also for a scan already gone.
   */
  async deleteScan(pumpCenterId: string, perfusionId: string, scanId: string): Promise<void> {
    const uri = `${this.pumpEndpoint}/${pumpCenterId}/case/${perfusionId}/scan/${scanId}`;
    const apiClient = signedApiClient('DELETE', uri);
    await apiClient.delete(uri);
  }

  /**
   * `GET pump/:pumpCenterId/case/:perfusionId/scan/:scanId/frame/:frameIndex` as an object URL.
   *
   * The frames live in a private bucket, so they are fetched through the signed API like any other
   * blob rather than linked to directly. Callers own the returned URL and must revoke it.
   *
   * `width` asks the backend to resize on the way out — use it for thumbnails, omit it for the
   * full-resolution capture.
   */
  async getFrameUrl(
    pumpCenterId: string,
    perfusionId: string,
    scanId: string,
    frameIndex: number,
    width?: number
  ): Promise<string> {
    const query = width ? `?width=${width}` : '';
    const uri = `${this.pumpEndpoint}/${pumpCenterId}/case/${perfusionId}/scan/${scanId}/frame/${frameIndex}${query}`;
    const apiClient = signedApiClient('GET', uri);
    // Passed per request rather than set on the shared client's defaults, which would leak into
    // every other call made while this one is in flight.
    const response = await apiClient.get(uri, { responseType: 'blob' });

    return window.URL.createObjectURL(response.data as Blob);
  }
}

export default new PerfusionScanService();
